<template>
    <WelcomeBox>
        <PromoNotice />

        <div class="register">
            <RegisterForm
                :on-progress="onProgress"
                :on-submit="onSubmit"
                :on-complete="onComplete"
            />

            <ProgressBar
                :value="progressBarValue - 1"
                :max="progressBarMax - 1"
                progress-color="#632ADB"
            />
        </div>

        <template v-slot:footer>
            <span class="login-link">
                Already have a Fusebox account? <router-link to="/login">Click here</router-link>.
            </span>
        </template>
    </WelcomeBox>
</template>

<script>

/**
 * External dependencies.
 */
import * as reCAPTCHA from 'recaptcha-v3';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import { first, mapValues } from 'lodash';

/**
 * Internal dependencies.
 */
import { WelcomeBox, ProgressBar, RegisterForm } from '@/components';
import PromoNotice from '@/components/PromoNotice/PromoNotice.vue';
import * as config from '@/components/RegisterForm/config';
import { ACTION_STEPS, ACTION } from '@/components/ShowModal/config';

export default {
    components: {
        RegisterForm,
        WelcomeBox,
        ProgressBar,
        PromoNotice,
    },

    data: () => ({
        isSubmitting: false,
    }),

    computed: {
        ...mapGetters('register', [
            'hasError',
        ]),

        ...mapState('register', [
            'step',
            'stepsCount',
        ]),

        ...mapState('register', {
            userData: state => state.data,
        }),

        /**
         * Gets the progress bar value.
         *
         * @returns {number} Progress bar value.
         */
        progressBarValue() {
            return this.step;
        },

        /**
         * Gets the progress bar max value.
         *
         * @returns {number} Progress bar max value.
         */
        progressBarMax() {
            return this.stepsCount + ACTION_STEPS[ACTION.CREATE].length;
        },

        /**
         * Returns reCAPTCHA site key.
         *
         * @returns {string} reCAPTCHA site key.
         */
        captchaSiteKey() {
            return document.head
                .querySelector('meta[name=google-recaptcha-site-key]')
                ?.getAttribute('content') ?? '';
        },
    },

    /**
     * Fires after component is created.
     */
    async created() {
        this.setTransitionEnabled(false);
        this.setErrors({});
        this.setData({});
        this.setStep({ step: config.REGISTER_STEP_USER_DATA, previousStep: 0 });
        this.setTransitionEnabled(true);

        if (! reCAPTCHA.getInstance()) {
            await reCAPTCHA.load(this.captchaSiteKey);
        }

        reCAPTCHA.getInstance().showBadge();
    },

    /**
     * Fires after components is unmounted.
     */
    unmounted() {
        reCAPTCHA.getInstance().hideBadge();
    },

    methods: {
        ...mapMutations(['changeUIState']),

        ...mapActions('register', [
            'nextStep',
            'registerUser',
            'clearErrors',
        ]),

        ...mapMutations('register', [
            'setData',
            'setIsRegistering',
            'setErrors',
            'setStep',
            'setTransitionEnabled',
            'setRecentlyRegistered',
        ]),

        ...mapActions('show/modal', {
            openShowModal: 'openModal',
        }),

        ...mapMutations('show/modal', {
            setShowModalFreshState: 'setFreshState',
        }),

        /**
         * Handles register progress.
         */
        onProgress() {
            this.nextStep();
        },

        /**
         * Handles register submit.
         */
        async onSubmit() {
            this.clearErrors();
            this.setIsRegistering(true);

            const token = await reCAPTCHA.getInstance().execute('register');
            const { status, data } = await this.registerUser({
                'g-recaptcha-response': token,
            });

            this.setIsRegistering(false);

            if (422 === status) {
                this.setErrors(
                    mapValues(data.errors ?? {}, (error) => {
                        return Array.isArray(error) ? first(error) : error;
                    })
                );

                if (this.hasError('name') || this.hasError('email')) {
                    this.setStep({ step: config.REGISTER_STEP_USER_DATA });
                }

                return false;
            } else if (400 <= status) {
                this.setErrors({
                    generic: data.message ?? 'Something went wrong, please try again.',
                });

                return false;
            }

            return true;
        },

        /**
         * Handles register completion.
         */
        onComplete() {
            // Full page load (not a client-side push) so the dashboard boots
            // from a clean, authenticated state. A router.push here hit the
            // same lazy-load race as login: lazy-loaded data fetched
            // mid-transition is never retried, leaving the dashboard spinning
            // until a manual refresh. The add-show onboarding modal reopens on
            // load via Dashboard.vue::newUserModals() (driven by has_shows).
            window.location.assign('/');
        },
    },
};
</script>
<style scoped lang="scss">
    @use './Register.scss';
</style>
